import { Alert, Badge, Group, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { EntitySelect } from '../../components/EntityField';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatFxRate, formatMoney } from '../../lib/format';

/**
 * TWO QUESTIONS ABOUT LEMPIRA PRICES, ON THE SCREEN THE OWNER ALREADY OPENS.
 *
 * The menu is priced in dollars and the lempira figure is worked out at the rate when a guest pays.
 * Locking a lempira price is a capability he asked for by name — "for that $3 beer if we decide that
 * we want to lock it to L80, we should be able to do that rather than it calculating at L81 or L79
 * as the exchange rate flocculates" — and it is a capability, not the normal state of a menu.
 *
 * Which means two things have to be visible and neither was:
 *
 *   1. **Which prices are locked, and how far the rate has walked away from them.** A beer locked at
 *      L80 against $3.00 is almost exactly the rate today. At 30 lempiras to the dollar the same lock
 *      is 11% under what the dollar price now comes to. That is not automatically wrong — it is what
 *      a lock is FOR — but it is his decision and he cannot take it if nothing tells him. Nothing on
 *      this page adjusts anything, and nothing ever will.
 *   2. **What one dish actually costs**, as he asks it: the default dollar price, an optional
 *      lempira price, and any time-based pricing, per size. A product here can have fourteen sizes,
 *      and the price of "Rum" is not a number the register will ever show.
 */

interface DriftRow {
  priceEntryId: string;
  priceListName: string;
  targetName: string | null;
  usdMinor: number | null;
  pinnedHnlMinor: number;
  pinnedAt: string | null;
  pinnedBy: string | null;
  pinnedByName: string | null;
  pinnedRateMicro: number | null;
  pinReason: string | null;
  derivedHnlMinor: number | null;
  driftBp: number | null;
  currentRateMicro: number | null;
}

interface ItemPricing {
  itemId: string;
  name: string;
  fxRateMicro: number | null;
  variants: {
    variantId: string;
    name: string;
    usdMinor: number | null;
    hnl: { minor: number; pinnedAt: string | null; pinnedBy: string | null; reason: string | null } | null;
    derivedHnlMinor: number | null;
    scheduled: {
      windowId: string;
      windowName: string;
      usdMinor: number | null;
      hnl: { minor: number } | null;
      qtyTiers: { minQty: number; usdMinor?: number | null; hnlMinor?: number | null }[] | null;
    }[];
  }[];
}

/** A signed basis-point gap read as a sentence rather than as a number nobody converts in their head. */
function driftSentence(row: DriftRow): string {
  if (row.driftBp === null || row.derivedHnlMinor === null) {
    return 'No dollar price beside it, so there is nothing to compare it against.';
  }
  const percent = Math.abs(row.driftBp / 100).toFixed(1);
  if (Math.abs(row.driftBp) < 100) return 'In step with the rate today.';
  return row.driftBp < 0
    ? `${percent}% below what the dollar price comes to today.`
    : `${percent}% above what the dollar price comes to today.`;
}

/**
 * The NAME, never the id. The id is an Entra object GUID in production and the string
 * `migration:0085` on everything carried over — neither of which answers "who locked this price".
 */
function whenAndWho(row: DriftRow): string {
  const on = row.pinnedAt
    ? new Date(row.pinnedAt).toLocaleDateString('en-US', { dateStyle: 'medium' })
    : null;
  const at = row.pinnedRateMicro ? formatFxRate(row.pinnedRateMicro) : null;
  const parts = [
    on ? `Set ${on}` : null,
    row.pinnedByName ? `by ${row.pinnedByName}` : null,
    at ? `when the rate was ${at}` : null,
  ];
  return parts.filter(Boolean).join(' ');
}

function LockedPrices() {
  const locationId = useLocationId();
  const drift = useQuery({
    queryKey: ['pin-drift', locationId],
    queryFn: () =>
      rawApi<DriftRow[]>(
        'GET',
        `/admin/v1/pricing/pin-drift?locationId=${encodeURIComponent(locationId ?? '')}`,
      ),
    enabled: Boolean(locationId),
  });
  const rows = drift.data ?? [];

  return (
    <Paper withBorder p="md" mb="lg">
      <Title order={4}>Locked lempira prices</Title>
      <Text size="sm" c="dimmed" mb="md">
        Every price where you chose the lempira figure yourself instead of letting it follow the exchange
        rate. Nothing here changes a price — it only shows you where the rate has moved since you set one.
      </Text>
      {rows.length === 0 ? (
        <Alert color="gray">
          Nothing is locked. Every lempira figure is worked out from the dollar price at the rate on file when
          the guest pays.
        </Alert>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Item</Table.Th>
              <Table.Th>Where</Table.Th>
              <Table.Th>Dollar price</Table.Th>
              <Table.Th>Locked at</Table.Th>
              <Table.Th>Comes to today</Table.Th>
              <Table.Th>Gap</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.priceEntryId}>
                <Table.Td>
                  <Text>{row.targetName ?? '—'}</Text>
                  {row.pinReason ? (
                    <Text size="xs" c="dimmed">
                      {row.pinReason}
                    </Text>
                  ) : null}
                </Table.Td>
                <Table.Td>{row.priceListName}</Table.Td>
                <Table.Td>{formatMoney(row.usdMinor, 'USD')}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Text>{formatMoney(row.pinnedHnlMinor, 'HNL')}</Text>
                    <Badge size="sm" variant="light">
                      locked
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {whenAndWho(row)}
                  </Text>
                </Table.Td>
                <Table.Td>{formatMoney(row.derivedHnlMinor, 'HNL')}</Table.Td>
                <Table.Td>
                  <Text size="sm">{driftSentence(row)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}

function tierSentence(
  tiers: { minQty: number; usdMinor?: number | null; hnlMinor?: number | null }[],
): string {
  return tiers
    .map((t) => {
      const usd = t.usdMinor != null ? formatMoney(t.usdMinor, 'USD') : null;
      const hnl = t.hnlMinor != null ? formatMoney(t.hnlMinor, 'HNL') : null;
      return `${t.minQty} for ${[usd, hnl].filter(Boolean).join(' / ')}`;
    })
    .join(' · ');
}

function OneItemsPrices() {
  const [itemId, setItemId] = useState<string | null>(null);
  const pricing = useQuery({
    queryKey: ['item-pricing', itemId],
    queryFn: () => rawApi<ItemPricing>('GET', `/admin/v1/pricing/item-pricing/${itemId}`),
    enabled: Boolean(itemId),
  });

  return (
    <Paper withBorder p="md">
      <Title order={4}>What one dish costs</Title>
      <Text size="sm" c="dimmed" mb="md">
        Every price of one dish in one place, size by size: the dollar price, a lempira price if you set one,
        and any pricing that only runs at certain times.
      </Text>
      <Stack gap="md">
        <EntitySelect
          entity="menuItem"
          label="Which dish"
          value={itemId}
          onChange={(v: string | null) => setItemId(v)}
        />
        {pricing.data ? (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Size</Table.Th>
                <Table.Th>Dollar price</Table.Th>
                <Table.Th>Lempira price</Table.Th>
                <Table.Th>At certain times</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pricing.data.variants.map((v) => (
                <Table.Tr key={v.variantId}>
                  <Table.Td>{v.name}</Table.Td>
                  <Table.Td>
                    {v.usdMinor == null ? (
                      <Text size="sm" c="dimmed">
                        Not sold at an everyday price
                      </Text>
                    ) : (
                      formatMoney(v.usdMinor, 'USD')
                    )}
                  </Table.Td>
                  <Table.Td>
                    {v.hnl ? (
                      <Group gap="xs">
                        <Text>{formatMoney(v.hnl.minor, 'HNL')}</Text>
                        <Badge size="sm" variant="light">
                          locked
                        </Badge>
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">
                        about {formatMoney(v.derivedHnlMinor, 'HNL')} at today’s rate
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {v.scheduled.length === 0 ? (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    ) : (
                      <Stack gap={2}>
                        {v.scheduled.map((s) => (
                          <Text size="sm" key={s.windowId}>
                            <strong>{s.windowName}</strong>{' '}
                            {s.qtyTiers?.length
                              ? tierSentence(s.qtyTiers)
                              : [
                                  s.usdMinor != null ? formatMoney(s.usdMinor, 'USD') : null,
                                  s.hnl ? formatMoney(s.hnl.minor, 'HNL') : null,
                                ]
                                  .filter(Boolean)
                                  .join(' / ')}
                          </Text>
                        ))}
                      </Stack>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : null}
      </Stack>
    </Paper>
  );
}

export function LockedLempiraPrices() {
  return (
    <>
      <LockedPrices />
      <OneItemsPrices />
    </>
  );
}
